import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import { Calendar, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { DashboardContent } from '@/layouts/dashboard';
import { Filter, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion } from '@/components/ui/accordion';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PLANO_AULA } from '@/pages/dashboard/home-teacher/teacher-character';
import { PATHS } from '@/routes/paths';
import { classPlansApi } from '@/services/classPlans';
import { extractApiError } from '@/services/errors';
import { LessonPlanSmartSubject } from './lesson-plan-smart-subject';
import { QUARTER_LABEL } from './lesson-plan-smart-constants';

const tabs = [
  { name: 'Plano de aulas', slug: 'inicio', url: PATHS.LESSON_PLAN.START },
  { name: 'Favoritos', slug: 'favoritos', url: PATHS.LESSON_PLAN.FAVORITES }
];

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
}

export function LessonPlanSmart({ selectedTab, uri }) {
  const playfulTeacher = usePlayfulTeacher();

  const [subjects, setSubjects] = useState([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const [subjectsError, setSubjectsError] = useState(null);

  const [filterOptions, setFilterOptions] = useState({ years: [], quarters: [], shifts: [], curricularComponents: [] });

  const [openSubject, setOpenSubject] = useState(undefined);
  // Aulas do card expandido são pesadas e cacheáveis (GET /class_plans/:id/lessons) —
  // buscadas sob demanda só quando o usuário expande o acordeon, não na listagem.
  const [lessonsBySubject, setLessonsBySubject] = useState({});

  const filters = useFormik({
    initialValues: {
      searchTerm: '',
      year: 'blank',
      quarter: 'blank',
      shift: 'blank',
      component: 'blank'
    }
  });

  const debouncedSearchTerm = useDebouncedValue(filters.values.searchTerm.trim(), 500);

  useEffect(() => {
    let isMounted = true;
    classPlansApi
      .filters()
      .then(options => {
        if (isMounted) setFilterOptions(options);
      })
      .catch(error => toast.error(extractApiError(error, 'Erro ao carregar os filtros do plano de aula.')));
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsLoadingSubjects(true);
    setSubjectsError(null);

    classPlansApi
      .list({
        q: debouncedSearchTerm || undefined,
        year: filters.values.year !== 'blank' ? filters.values.year : undefined,
        quarter: filters.values.quarter !== 'blank' ? filters.values.quarter : undefined,
        shift: filters.values.shift !== 'blank' ? filters.values.shift : undefined,
        curricularComponent: filters.values.component !== 'blank' ? filters.values.component : undefined,
        favorite: selectedTab === 'favoritos'
      })
      .then(data => {
        if (!isMounted) return;
        setSubjects(data);
      })
      .catch(error => {
        if (!isMounted) return;
        setSubjectsError(extractApiError(error, 'Erro ao carregar o plano de aula.'));
      })
      .finally(() => {
        if (isMounted) setIsLoadingSubjects(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, debouncedSearchTerm, filters.values.year, filters.values.quarter, filters.values.shift, filters.values.component]);

  const fetchLessons = subjectId => {
    setLessonsBySubject(prev => ({ ...prev, [subjectId]: { ...prev[subjectId], isLoading: true, error: null } }));

    classPlansApi
      .lessons(subjectId)
      .then(months => {
        setLessonsBySubject(prev => ({ ...prev, [subjectId]: { months, isLoading: false, error: null } }));
      })
      .catch(error => {
        setLessonsBySubject(prev => ({
          ...prev,
          [subjectId]: { months: null, isLoading: false, error: extractApiError(error, 'Erro ao carregar as aulas deste plano.') }
        }));
      });
  };

  // Acordeon: quando o usuário expande um card, busca as aulas do plano de aula (GET /class_plans/:id/lessons).
  // Se o card for fechado, mantém os dados em cache (não limpa o state).
  const handleAccordionChange = subjectId => {
    setOpenSubject(subjectId);
    if (subjectId && !lessonsBySubject[subjectId]) {
      fetchLessons(subjectId);
    }
  };

  const toggleFavorite = async subject => {
    const nextFavorite = !subject.favorited;

    // Atualização otimista: o card responde na hora, e desfaz se a API falhar.
    setSubjects(prev => prev.map(item => (item.id === subject.id ? { ...item, favorited: nextFavorite } : item)));

    try {
      const updated = await classPlansApi.toggleFavorite(subject.id, nextFavorite);
      setSubjects(prev => {
        // Na aba de favoritos, desfavoritar remove o card da lista.
        if (selectedTab === 'favoritos' && !updated.favorited) {
          return prev.filter(item => item.id !== subject.id);
        }
        return prev.map(item => (item.id === subject.id ? updated : item));
      });
    } catch (error) {
      setSubjects(prev => prev.map(item => (item.id === subject.id ? { ...item, favorited: subject.favorited } : item)));
      toast.error(extractApiError(error, 'Erro ao favoritar o plano de aula.'));
    }
  };

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Plano de Aula Inteligente',
        isActive: true
      }
    ]
  };

  const hasActiveQuery = Boolean(debouncedSearchTerm || filters.values.year !== 'blank' || filters.values.quarter !== 'blank' || filters.values.shift !== 'blank' || filters.values.component !== 'blank');

  const yearOptions = useMemo(() => filterOptions.years.map(year => ({ id: String(year), name: String(year) })), [filterOptions.years]);
  const quarterOptions = useMemo(() => filterOptions.quarters.map(quarter => ({ id: quarter, name: QUARTER_LABEL[quarter] || quarter })), [filterOptions.quarters]);
  const shiftOptions = useMemo(() => filterOptions.shifts.map(shift => ({ id: shift, name: shift })), [filterOptions.shifts]);
  const componentOptions = useMemo(() => filterOptions.curricularComponents.map(component => ({ id: component, name: component })), [filterOptions.curricularComponents]);

  // Aba ativa chanfrada (relevo 3D igual aos botões), mesmo padrão de course-tabs.jsx.
  const playfulTab = playfulTeacher
    ? 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px'
    : undefined;

  return (
    <DashboardContent
      variant="column"
      breadcrumbs={breadcrumbs}
    >
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={PLANO_AULA}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={250}
            bubbleCorner="tl"
            bubbleMaxWidth={500}
            text="Aqui está seu plano de aula inteligente! 📚 Filtre por ano, trimestre, turno ou componente curricular e acesse o material da sua próxima aula."
          />
        </div>
      )}

      <Tabs
        value={selectedTab}
        className="w-full"
      >
        <TabsList className={cn('mb-4 w-fit', playfulTeacher && 'border border-neutral-lighter bg-white')}>
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.slug}
              value={tab.slug}
              className={playfulTab}
              asChild
            >
              <Link to={tab.url}>{tab.name}</Link>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Filter variant="plain">
        <FilterContent>
          <FilterInput
            label="Pesquisar"
            placeholder="Pesquisar objeto do conhecimento"
            type="search"
            name="search"
            onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
            wrapperClassName="md:w-72"
          />
          <FilterSelectNative
            blankLabel="Ano"
            label="Selecione um ano"
            value={filters.values.year}
            onChange={e => filters.setFieldValue('year', e.target.value)}
            icon={Calendar}
            options={yearOptions}
          />
          <FilterSelectNative
            blankLabel="Trimestre"
            label="Selecione um trimestre"
            value={filters.values.quarter}
            onChange={e => filters.setFieldValue('quarter', e.target.value)}
            icon={Calendar}
            options={quarterOptions}
          />
          <FilterSelectNative
            blankLabel="Turno"
            label="Selecione um turno"
            value={filters.values.shift}
            onChange={e => filters.setFieldValue('shift', e.target.value)}
            icon={Clock}
            options={shiftOptions}
          />
          <FilterSelectNative
            blankLabel="Componente curricular"
            label="Selecione um componente curricular"
            value={filters.values.component}
            onChange={e => filters.setFieldValue('component', e.target.value)}
            options={componentOptions}
            wrapperClassName="md:w-72"
          />
        </FilterContent>
      </Filter>

      {isLoadingSubjects ? (
        <Loader />
      ) : subjectsError ? (
        <EmptyState
          type="disapproved"
          text={subjectsError}
          variant="plain"
        />
      ) : subjects?.length > 0 ? (
        <Accordion
          type="single"
          collapsible
          value={openSubject}
          onValueChange={handleAccordionChange}
        >
          {subjects.map(subject => (
            <LessonPlanSmartSubject
              key={subject.id}
              subject={subject}
              isOpen={openSubject === subject.id}
              lessonsState={lessonsBySubject[subject.id]}
              onToggleFavorite={() => toggleFavorite(subject)}
            />
          ))}
        </Accordion>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          {selectedTab === 'favoritos' && !hasActiveQuery ? (
            <EmptyState
              type="data"
              text="Navegue pelos Planos de Aula e clique no coração para salvar os seus favoritos e encontrar tudo facilmente depois."
              title="Ops, sua lista de favoritos está vazia. "
              variant="plain"
              className="mx-auto w-[850px]"
            />
          ) : (
            <EmptyState
              type="data"
              title="Não encontramos nenhum resultado para sua busca."
              text="Tente ajustar os filtros ou utilizar palavras-chave diferentes."
              variant="plain"
              className="mx-auto w-[850px]"
            />
          )}
        </div>
      )}
    </DashboardContent>
  );
}
